import { useMutation } from '@apollo/client';
import { Check } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FormSchemaProvider } from '@procureai/ui-form-mui';
import { useUuid, useCustomSnackbar } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v7 as uuidv7 } from 'uuid';

import { PROCUREMENT_KNOWLEDGE_LIST_QUERY } from '../../ProcurementKnowledgeListQuery.gql';
import { ProcurementKnowledgeForm } from '../ProcurementKnowledgeForm/ProcurementKnowledgeForm';
import {
  ProcurementKnowledgeDocumentData,
  procurementKnowledgeDocumentDataDefault,
  procurementKnowledgeDocumentSchema,
} from '../ProcurementKnowledgeForm/ProcurementKnowledgeForm.schema';
import { getFragmentData } from '@/generated/gql';
import { ProcurementKnowledgeDocumentFragment, SvbrgDocumentVariant, SvbrgLanguage } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';

import {
  PROCUREMENT_KNOWLEDGE_ADD_MUTATION,
  PROCUREMENT_KNOWLEDGE_DOCUMENT_FRAGMENT,
} from './ProcurementKnowledgeAddMutation.gql';

export interface ProcurementKnowledgeAddDialogProps {
  open: boolean;
  onDocumentAdded?: (doc: ProcurementKnowledgeDocumentFragment) => void;
  onClose: () => void;
}

export const ProcurementKnowledgeAddDialog = (props: ProcurementKnowledgeAddDialogProps) => {
  const formId = useUuid();

  const { onError } = useCustomSnackbar();

  const { t } = useTranslation(I18nNamespace.knowledgeRepository, {
    keyPrefix: 'ProcurementKnowledge.ProcurementKnowledgeAddDialog',
  });

  // empty default values
  const defaultValues = useMemo(() => {
    return procurementKnowledgeDocumentDataDefault();
  }, []);

  // mutation to save the document
  const [addDocument, { loading: saving }] = useMutation(PROCUREMENT_KNOWLEDGE_ADD_MUTATION, {
    onError,
    refetchQueries: [{ query: PROCUREMENT_KNOWLEDGE_LIST_QUERY }],
  });

  return (
    <Dialog fullWidth maxWidth="sm" open={props.open} onClose={props.onClose}>
      <DialogTitle>{t('title')}</DialogTitle>

      <DialogContent>
        <FormSchemaProvider<ProcurementKnowledgeDocumentData>
          id={formId}
          schema={procurementKnowledgeDocumentSchema}
          defaultValues={defaultValues}
          onSubmit={async (data) => {
            const newId = uuidv7();

            const result = await addDocument({
              variables: {
                input: {
                  id: newId,
                  fileId: data.fileId,
                  title: data.title,
                  description: data.description,
                  url: data.url,
                  variant: data.variant as SvbrgDocumentVariant,
                  language: data.language as SvbrgLanguage,
                  content: data.content,
                },
              },
            });

            const document = result?.data?.sevenbridges.document.upload;
            if (document) {
              props.onDocumentAdded?.(getFragmentData(PROCUREMENT_KNOWLEDGE_DOCUMENT_FRAGMENT, document));
              props.onClose();
            }
          }}
        >
          <ProcurementKnowledgeForm />
        </FormSchemaProvider>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={props.onClose}>
          {t('cancelLabel')}
        </Button>
        <Button
          loading={saving}
          variant="contained"
          color="primary"
          type="submit"
          startIcon={<Check />}
          form={formId}
          disabled={saving}
        >
          {t('confirmLabel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
